import { matchPath, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { routesMetadata } from "../../../infrastructure/routes/routesMetadata";
import { useHistoryBack } from "../../../infrastructure/hooks/useHistoryBack";
import { DraggableBackButton } from "./DraggableBackButton";

/**
 * Floating "back" button rendered globally by the Layout on every page.
 *
 * Because the button is draggable (and remembers where the user parks it) it is
 * a handy, unobtrusive control to have available everywhere, so it is shown on
 * all routes rather than only on detail pages.
 *
 * Navigation relies on the real browser history, so it "remembers" where the
 * user came from automatically (class -> user -> back returns to the class;
 * list -> user -> back returns to the list). When there is no in-app history to
 * go back to (a fresh entry point / cold PWA start) it falls back to the
 * route's `backFallbackPath` if one is defined, and otherwise does nothing —
 * just like a browser's back button at the start of its history.
 *
 * Shown on all viewport widths. It is primarily aimed at the installed PWA
 * (which has no browser back button), but it is also convenient on desktop.
 *
 * The visuals and drag-to-reposition behaviour live in {@link DraggableBackButton};
 * this component only supplies the route-aware "back" action. The booking
 * wizards render their own {@link DraggableBackButton} instance because they
 * manage their step history themselves (and are outside this layout).
 */
export const BackButton = () => {
  const location = useLocation();

  const currentRoute = useMemo(
    () => routesMetadata.find((route) => matchPath(route.path, location.pathname)),
    [location.pathname],
  );

  const onBack = useHistoryBack(currentRoute?.backFallbackPath);

  return <DraggableBackButton onBack={onBack} />;
};
